import React from 'react';
import { PolymorphicWithoutRef } from '../../primitives/polymorphic';
import { useDniProvider } from '../../../contexts/dni-provider';

/*
* Memory-only persistence for Invoca/other provider
* phone number replacements, to reduce pop-in as the
* user browses the site.
* */

class PhoneNumberStore {
    numbers: Record<string, string> = {};
    set(originalNumber: string, replacedNumber: string): void {
        if (originalNumber !== replacedNumber) {
            this.numbers[originalNumber] = replacedNumber;
        }
    }
}

const phoneNumberCache = new PhoneNumberStore();

const PhoneNumber = <C extends React.ElementType = 'a'>({ children, as, ...rest }: PolymorphicWithoutRef<C, {
    children?: string
}>): JSX.Element | null => {
    const Component = as || 'a';
    const ref = React.useRef<HTMLElement>(null);

    const [refresh, setRefresh] = React.useState<number>(0);

    const {
        numberMap,
    } = useDniProvider();

    const numberToUse = (() => {
        if (children) {
            if (numberMap[children]) {
                return numberMap[children];
            }
            if (phoneNumberCache.numbers[children]) {
                return phoneNumberCache.numbers[children];
            }
            return children;
        }
        return null;
    })();

    const findReplacedNumber = React.useCallback((currentElement: HTMLElement | null): void => {
        if (currentElement && typeof currentElement === 'object' && children) {
            if ('innerText' in currentElement) {
                const { innerText } = currentElement;
                phoneNumberCache.set(
                    children,
                    innerText as string,
                );
            }
        }
    }, [children]);

    React.useEffect(
        // eslint-disable-next-line arrow-body-style
        () => {
            /*
            * Start polling for number change if no update
            * has occured
            * */
            if (children && !phoneNumberCache.numbers[children]) {
                setRefresh(4);
            }
            /*
            * Grab the correct number on unmount
            * */
            const preservedRef = ref.current;
            return () => {
                findReplacedNumber(preservedRef);
            };
        },
        [children, findReplacedNumber],
    );

    React.useEffect(() => {
        let currentTimer: string | number | NodeJS.Timeout | undefined;
        if (refresh > 0) {
            currentTimer = setTimeout(() => {
                findReplacedNumber(ref.current);
                setRefresh(e => e - 1);
            }, 500);
        }
        return () => {
            clearTimeout(currentTimer);
        };
    }, [findReplacedNumber, refresh]);

    if (!numberToUse) {
        return null;
    }

    return (
        <Component
            ref={ref}
            {...rest}
            to={`tel:${numberToUse}`}
            href={`tel:${numberToUse}`}
        >
            {numberToUse || children}
        </Component>
    );
};

export default PhoneNumber;
